import React, { Component } from "react";
import { Modal, Button } from "react-bootstrap";
import { withApollo } from "react-apollo";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";

// Attendance-regularization "Notify Students" campaign — mirrors the exam-result
// flow: stage a WhatsApp campaign to the day's absentees' parents, then track
// delivery (sent/delivered/read/failed) and resend to the ones that failed.
const CREATE_ATTENDANCE_CAMPAIGN = gql`
  mutation($input: CreateAttendanceCampaignInput!) {
    createAttendanceCampaign(input: $input) {
      status
      total_recipients
      sent_count
      failed_count
      absent_count
    }
  }
`;

const RESEND_FAILED_ATTENDANCE_CAMPAIGN = gql`
  mutation($input: ResendFailedAttendanceCampaignInput!) {
    resendFailedAttendanceCampaign(input: $input) {
      status
      total_recipients
    }
  }
`;

const GET_ATTENDANCE_CAMPAIGNS = gql`
  query($institute_id: Int!, $branch_id: Int, $date: String) {
    getAttendanceCampaigns(institute_id: $institute_id, branch_id: $branch_id, date: $date) {
      id
      campaign_name
      workflow_status_id
      attempted
      sent
      delivered
      read
      failed
    }
  }
`;

// Template preview for THIS modal comes from notification_template (the same source the
// send path uses), resolved per channel: SMS -> sms_templates, WhatsApp -> wa_template.
// The old getExamEventTemplates (external service, keyed by employee) could show a template
// that didn't match what actually sends.
const GET_ATTENDANCE_NOTIFY_TEMPLATE = gql`
  query($institute_id: Int!, $channel: String!) {
    getAttendanceNotifyTemplate(institute_id: $institute_id, channel: $channel) {
      event_code
      channel
      found
      template_name
      language
      body
      provider_template_id
    }
  }
`;

const CHANNELS = [
  { key: "whatsapp", label: "WhatsApp", icon: "fa-whatsapp", desc: "Approved absence template to each parent" },
  { key: "sms", label: "SMS", icon: "fa-sms", desc: "DLT absence template to each parent" },
];

class NotifyAttendanceStudentsModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      channel: "whatsapp", sending: false, result: null, error: "",
      templates: null, templatesLoading: false, showTemplate: false,
      campaigns: [], campaignsLoading: false, activeTab: "recipients",
      resending: false, resendNote: "",
      // Section targeting — empty = every section in the branch.
      sectionSelected: [],
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const justOpenedPreview = this.state.showTemplate && !prevState.showTemplate;
    // Refetch on preview open OR when the channel changes while the preview is open —
    // SMS and WhatsApp have different templates, so the shown one must follow the channel.
    const channelChangedWhileOpen = this.state.showTemplate && prevState.channel !== this.state.channel;
    if (this.props.show && this.state.showTemplate && (justOpenedPreview || channelChangedWhileOpen) && !this.state.templatesLoading) {
      this.fetchTemplates();
    }
    if (this.props.show && !prevProps.show) {
      this.fetchCampaigns();
    }
  }

  instId = () => parseInt(Cookies.get("institutionid")) || 0;
  userId = () => parseInt(Cookies.get("user_id") || Cookies.get("id")) || 0;

  fetchCampaigns = async () => {
    this.setState({ campaignsLoading: true });
    try {
      const res = await this.props.client.query({
        query: GET_ATTENDANCE_CAMPAIGNS,
        variables: { institute_id: this.instId(), branch_id: parseInt(this.props.branchId) || null, date: this.props.date || null },
        fetchPolicy: "network-only",
      });
      this.setState({ campaigns: (res.data && res.data.getAttendanceCampaigns) || [], campaignsLoading: false });
    } catch (e) {
      this.setState({ campaigns: [], campaignsLoading: false });
    }
  };

  fetchTemplates = async () => {
    this.setState({ templatesLoading: true });
    try {
      const res = await this.props.client.query({
        query: GET_ATTENDANCE_NOTIFY_TEMPLATE,
        variables: { institute_id: this.instId(), channel: this.state.channel },
        fetchPolicy: "network-only",
      });
      const t = res.data && res.data.getAttendanceNotifyTemplate;
      // Keyed by event_code so the existing `templates.attendance_absent` lookup keeps working.
      const map = t && t.found ? { [t.event_code]: t } : {};
      this.setState({ templates: map, templatesLoading: false });
    } catch (e) {
      this.setState({ templates: {}, templatesLoading: false });
    }
  };

  reset = () => this.setState({ channel: "whatsapp", sending: false, result: null, error: "", templates: null, showTemplate: false, activeTab: "recipients", resendNote: "", sectionSelected: [] });

  handleClose = () => { this.reset(); this.props.onHide(); };

  // Absentees, narrowed to the picked sections (none picked = all).
  getRecipients = () => {
    const all = this.props.students || [];
    const sel = this.state.sectionSelected;
    return sel.length ? all.filter((s) => sel.map(Number).includes(Number(s.section_id))) : all;
  };

  // Distinct sections present in the day's absentees, with names resolved by the parent.
  sectionOptions = () => {
    const seen = new Map();
    (this.props.students || []).forEach((s) => {
      const id = Number(s.section_id);
      if (!id || seen.has(id)) return;
      const name = this.props.sectionName ? this.props.sectionName(id) : `Section ${id}`;
      seen.set(id, { id, name: name || `Section ${id}` });
    });
    return Array.from(seen.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  };

  toggleSection = (id) => {
    this.setState((s) => {
      const has = s.sectionSelected.map(Number).includes(Number(id));
      return { sectionSelected: has ? s.sectionSelected.filter((x) => Number(x) !== Number(id)) : [...s.sectionSelected, Number(id)] };
    });
  };

  handleSend = async () => {
    this.setState({ sending: true, error: "", result: null });
    try {
      const res = await this.props.client.mutate({
        mutation: CREATE_ATTENDANCE_CAMPAIGN,
        variables: {
          input: {
            institute_id: this.instId(),
            branch_id: parseInt(this.props.branchId) || null,
            date: this.props.date,
            channel: this.state.channel,
            inserted_by: this.userId(),
            // Only these sections' absentees; omitted (null) = the whole branch.
            section_ids: this.state.sectionSelected.length ? this.state.sectionSelected.map(Number) : null,
          },
        },
      });
      const c = (res.data && res.data.createAttendanceCampaign) || {};
      this.setState({
        result: { total: c.total_recipients || 0, channel: this.state.channel },
        sending: false,
        activeTab: "analysis",
      }, this.fetchCampaigns);
    } catch (err) {
      this.setState({
        error: (err.graphQLErrors && err.graphQLErrors.map((x) => x.message).join(", ")) || "Failed to send notifications.",
        sending: false,
      });
    }
  };

  handleResendFailed = async () => {
    this.setState({ resending: true, resendNote: "", error: "" });
    try {
      const res = await this.props.client.mutate({
        mutation: RESEND_FAILED_ATTENDANCE_CAMPAIGN,
        variables: {
          input: {
            institute_id: this.instId(),
            branch_id: parseInt(this.props.branchId) || null,
            date: this.props.date,
            channel: this.state.channel,
            inserted_by: this.userId(),
            // Only these sections' absentees; omitted (null) = the whole branch.
            section_ids: this.state.sectionSelected.length ? this.state.sectionSelected.map(Number) : null,
          },
        },
      });
      const c = (res.data && res.data.resendFailedAttendanceCampaign) || {};
      const note = c.status === "no_failures"
        ? "No failed messages to resend."
        : `Re-queued ${c.total_recipients || 0} failed recipient(s).`;
      this.setState({ resending: false, resendNote: note }, this.fetchCampaigns);
    } catch (err) {
      this.setState({
        resending: false, resendNote: "",
        error: (err.graphQLErrors && err.graphQLErrors.map((x) => x.message).join(", ")) || "Failed to resend.",
      });
    }
  };

  render() {
    const { show, dateLabel } = this.props;
    const { channel, sending, result, error } = this.state;
    const recipientList = this.getRecipients();
    const templates = this.state.templates || {};
    const tpl = templates.attendance_absent;

    return (
      <Modal show={show} onHide={this.handleClose} centered size="md">
        <Modal.Header closeButton style={{ background: "#0ea5e9", color: "#fff", border: "none" }}>
          <Modal.Title style={{ fontSize: "1.05rem", fontWeight: 600 }}>
            <i className="fas fa-bell mr-2" /> Notify Students — Attendance
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="mb-3" style={{ fontSize: 13.5, color: "#475569" }}>
            Notify parents of <strong>absent</strong> students for <strong>{dateLabel || this.props.date}</strong>
            {" "}— <strong>{recipientList.length}</strong> recipient(s).
          </p>

          {this.props.testNumber ? (
            <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "8px 12px", fontSize: 12.5, color: "#dc2626", fontWeight: 700, marginBottom: 12 }}>
              ⚠ Your Command Center test number ({this.props.testNumber}) does NOT apply here —
              this campaign sends to the REAL parent numbers listed below.
            </div>
          ) : null}

          {result ? (
            <div style={{ background: "#ecfeff", border: "1px solid #a5f3fc", borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ fontWeight: 700, color: "#0e7490", marginBottom: 6 }}>
                <i className="fas fa-paper-plane mr-2" />Queued ({result.channel})
              </div>
              <div style={{ fontSize: 13, color: "#334155" }}>
                Queued: <strong style={{ color: "#0e7490" }}>{result.total}</strong> recipient(s)
              </div>
              <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 6 }}>
                Delivery is processing — see the <strong>Campaign Analysis</strong> tab for Sent / Delivered / Read.
              </div>
            </div>
          ) : (
            <>
              {/* Section targeting — none picked = every section in the branch. */}
              {this.sectionOptions().length > 1 ? (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.6px" }}>
                    Sections {this.state.sectionSelected.length ? `(${this.state.sectionSelected.length} selected)` : "(all)"}
                  </label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                    {this.sectionOptions().map((s) => {
                      const on = this.state.sectionSelected.map(Number).includes(Number(s.id));
                      const count = (this.props.students || []).filter((x) => Number(x.section_id) === Number(s.id)).length;
                      return (
                        <button key={s.id} type="button" onClick={() => this.toggleSection(s.id)}
                          style={{
                            padding: "5px 11px", borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: "pointer",
                            border: `1.5px solid ${on ? "#0ea5e9" : "#e5e7eb"}`,
                            background: on ? "#e0f2fe" : "#fff", color: on ? "#0369a1" : "#475569",
                          }}>
                          {on ? <i className="fas fa-check mr-1" /> : null}{s.name} ({count})
                        </button>
                      );
                    })}
                  </div>
                  {this.state.sectionSelected.length ? (
                    <button type="button" onClick={() => this.setState({ sectionSelected: [] })}
                      style={{ marginTop: 6, border: "none", background: "none", color: "#0ea5e9", fontSize: 11.5, fontWeight: 600, cursor: "pointer", padding: 0 }}>
                      Clear — notify all sections
                    </button>
                  ) : null}
                </div>
              ) : null}

              <label style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.6px" }}>Channel</label>
              <div style={{ display: "flex", flexDirection: "row", gap: 8, marginTop: 8 }}>
                {CHANNELS.map((c) => (
                  <div
                    key={c.key}
                    title={c.desc}
                    onClick={() => !sending && this.setState({ channel: c.key })}
                    style={{
                      flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "12px 8px", borderRadius: 10, cursor: "pointer", textAlign: "center",
                      border: `1.5px solid ${channel === c.key ? "#0ea5e9" : "#e5e7eb"}`,
                      background: channel === c.key ? "#ecfeff" : "#fff",
                    }}
                  >
                    <i className={`${c.key === "whatsapp" ? "fab" : "fas"} ${c.icon}`} style={{ fontSize: 20, color: channel === c.key ? "#0ea5e9" : "#94a3b8" }} />
                    <div style={{ fontWeight: 600, fontSize: 12.5, color: channel === c.key ? "#0e7490" : "#1f2937" }}>{c.label}</div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: 16 }}>
                <button
                  type="button"
                  onClick={() => this.setState({ showTemplate: !this.state.showTemplate })}
                  style={{ background: "none", border: "none", padding: 0, color: "#0ea5e9", fontSize: 12, fontWeight: 700, cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.6px" }}
                >
                  <i className={`fas fa-chevron-${this.state.showTemplate ? "up" : "down"} mr-1`} />
                  {this.state.showTemplate ? "Hide" : "View"} template
                </button>
                {this.state.showTemplate && (
                  this.state.templatesLoading ? (
                    <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}><span className="spinner-border spinner-border-sm mr-2" />Loading template…</div>
                  ) : (
                    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", marginTop: 8, background: "#fafafa" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#0ea5e9", marginBottom: 4 }}>Absentee · <span style={{ color: "#94a3b8" }}>attendance_absent</span></div>
                      {tpl ? (
                        <>
                          <div style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>{tpl.template_name} {tpl.language ? <span style={{ color: "#94a3b8", fontWeight: 400 }}>({tpl.language})</span> : null}</div>
                          {tpl.body ? <div style={{ fontSize: 12, color: "#334155", whiteSpace: "pre-wrap", marginTop: 4 }}>{tpl.body}</div> : null}
                        </>
                      ) : (
                        <div style={{ fontSize: 12, color: "#dc2626" }}>No template mapped for <strong>attendance_absent</strong>.</div>
                      )}
                    </div>
                  )
                )}
              </div>

              {error ? <div className="alert alert-danger py-2 mt-3 mb-0" style={{ fontSize: 12.5 }}>{error}</div> : null}
            </>
          )}

          {/* Tabs: Recipients | Campaign Analysis */}
          <div style={{ marginTop: 18 }}>
            <div style={{ display: "flex", gap: 4, borderBottom: "2px solid #eef2f7" }}>
              {[
                { key: "recipients", label: `Recipients (${recipientList.length})` },
                { key: "analysis", label: "Campaign Analysis" },
              ].map((t) => {
                const active = this.state.activeTab === t.key;
                return (
                  <button key={t.key} type="button" onClick={() => this.setState({ activeTab: t.key })}
                    style={{ background: "none", border: "none", padding: "8px 14px", marginBottom: -2, cursor: "pointer", fontSize: 12.5, fontWeight: 700, color: active ? "#0ea5e9" : "#94a3b8", borderBottom: `2px solid ${active ? "#0ea5e9" : "transparent"}` }}>
                    {t.label}
                  </button>
                );
              })}
            </div>

            {this.state.activeTab === "recipients" ? (
              <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #e5e7eb", borderTop: "none", borderRadius: "0 0 8px 8px" }}>
                {recipientList.length === 0 ? (
                  <div style={{ padding: 14, fontSize: 12.5, color: "#94a3b8", textAlign: "center" }}>No absentees today.</div>
                ) : recipientList.map((s, i) => {
                  const phone = s.father_phone || s.mother_phone || s.student_mobile || "-";
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", borderBottom: "1px solid #f3f4f6", fontSize: 12.5 }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#f59e0b", flexShrink: 0 }} />
                      <span style={{ color: "#1f2937", fontWeight: 600 }}>{s.student_name || s.admission_no || "-"}</span>
                      <span style={{ color: "#94a3b8", marginLeft: "auto" }}>{phone}</span>
                      <span style={{ color: "#f59e0b", fontWeight: 600, minWidth: 52, textAlign: "right" }}>Absent</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #e5e7eb", borderTop: "none", borderRadius: "0 0 8px 8px" }}>
                {this.state.campaignsLoading ? (
                  <div style={{ padding: 14, fontSize: 12, color: "#94a3b8", textAlign: "center" }}><span className="spinner-border spinner-border-sm mr-2" />Loading…</div>
                ) : this.state.campaigns.length === 0 ? (
                  <div style={{ padding: 14, fontSize: 12.5, color: "#94a3b8", textAlign: "center" }}>No campaigns yet.</div>
                ) : (
                  <>
                    {(() => {
                      const totalFailed = this.state.campaigns.reduce((n, c) => n + (c.failed || 0), 0);
                      return (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#fff", borderBottom: "1px solid #f3f4f6" }}>
                          <span style={{ fontSize: 12, color: totalFailed ? "#dc2626" : "#94a3b8", fontWeight: 600 }}>
                            <i className="fas fa-exclamation-triangle mr-1" />{totalFailed} failed
                          </span>
                          {this.state.resendNote ? <span style={{ fontSize: 11.5, color: "#16a34a", marginLeft: 4 }}>{this.state.resendNote}</span> : null}
                          <button type="button" disabled={!totalFailed || this.state.resending}
                            onClick={this.handleResendFailed}
                            style={{ marginLeft: "auto", padding: "5px 12px", borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: (!totalFailed || this.state.resending) ? "not-allowed" : "pointer",
                              border: "1.5px solid #0ea5e9", background: (!totalFailed || this.state.resending) ? "#ecfeff" : "#0ea5e9", color: (!totalFailed || this.state.resending) ? "#7dd3fc" : "#fff" }}>
                            {this.state.resending ? <><span className="spinner-border spinner-border-sm mr-1" />Resending…</> : <><i className="fas fa-redo mr-1" />Resend Failed</>}
                          </button>
                        </div>
                      );
                    })()}
                    <div style={{ display: "flex", padding: "6px 12px", background: "#f8fafc", fontSize: 10.5, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      <span style={{ flex: 1 }}>Campaign</span>
                      <span style={{ width: 44, textAlign: "center" }}>Sent</span>
                      <span style={{ width: 62, textAlign: "center" }}>Delivered</span>
                      <span style={{ width: 44, textAlign: "center" }}>Read</span>
                      <span style={{ width: 48, textAlign: "center" }}>Failed</span>
                    </div>
                    {this.state.campaigns.map((c) => (
                      <div key={c.id} style={{ display: "flex", alignItems: "center", padding: "7px 12px", borderTop: "1px solid #f3f4f6", fontSize: 12 }}>
                        <span style={{ flex: 1, color: "#1f2937", fontWeight: 600 }}>#{c.id} · {c.campaign_name}</span>
                        <span style={{ width: 44, textAlign: "center", color: "#16a34a", fontWeight: 600 }}>{c.sent}</span>
                        <span style={{ width: 62, textAlign: "center", color: "#0891b2", fontWeight: 600 }}>{c.delivered}</span>
                        <span style={{ width: 44, textAlign: "center", color: "#0ea5e9", fontWeight: 600 }}>{c.read}</span>
                        <span style={{ width: 48, textAlign: "center", color: c.failed ? "#dc2626" : "#94a3b8", fontWeight: 600 }}>{c.failed}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          {result ? (
            <Button variant="success" onClick={this.handleClose}>Done</Button>
          ) : (
            <>
              <Button variant="light" onClick={this.handleClose} disabled={sending}>Cancel</Button>
              <Button style={{ background: "#0ea5e9", borderColor: "#0ea5e9" }} onClick={this.handleSend} disabled={sending || recipientList.length === 0}>
                {sending ? <><span className="spinner-border spinner-border-sm mr-2" />Sending…</> : <><i className="fas fa-paper-plane mr-1" /> Send</>}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    );
  }
}

export default withApollo(NotifyAttendanceStudentsModal);
